import { useState, useCallback, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom";

import { ArrowLeft, Check, X } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { logger } from "./helpers/logHandler";
import { playSuccess, playError } from "./helpers/audioPlayer";
import { logoutProcessor } from "./helpers/logoutProcessor";
import HiddenScanInput from "./HiddenScanInput";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import ZoneSorterLocationBoxes from "./zone-sorter-location-boxes";
import { zoneDispatchReasonKey } from "./sorterTwo/zoneDispatchReasons";
import {
    useProgressTimer,
    useStreak,
    useBestTime,
    useSessionScore,
    ProgressBar,
    TimeBonusPopup,
    ScoreDisplay,
} from "./gamification";
import { apiFetch } from "../api/apiFetch";
import "../styles/scansheet.css";
import "../styles/zonestorage.css";

type zoneSorter1 = {
    warehouse: string,
    bay: string,
    resetFunc: () => void
}

const TIMER_DURATION_MS = 6000;

// Does this scan look like a tote / tag rather than an item or a slot? Same
// discrimination the singles-lane screen uses for its tag scan: slot codes
// start NPS/PLS, LPNs start LPN, scansheet items start "sp", and tote ids
// are short.
const isToteShaped = (v: string) =>
    v !== "" && v.length <= 8
    && !["NPS", "PLS", "LPN"].includes(v.slice(0, 3))
    && v.slice(0, 2).toLowerCase() !== "sp";

const ZoneStorageSorter1 = ({ warehouse, bay, resetFunc }: zoneSorter1) => {
    const navigator = useNavigate();
    const { t } = useTranslation();

    const [scan, setScan] = useState<string>("");
    const [location, set_Location] = useState<string[]>([]);
    const [item, set_item] = useState<string>("");
    const [itemWeight, set_itemWeight] = useState<number>(0);
    const [lastScannedItem, set_lastScannedItem] = useState<string>("N/A");
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [errorPopUp, set_errorPopUp] = useState<boolean>(false);
    const [errorRescan, set_errorRescan] = useState<boolean>(false);

    // Unsold Reauction → Portal stock move (BE migration 237). The bay's
    // reauction tote: null = none yet, so the next reauction item asks for
    // one. `reauctionItem` is the reauction unit in the operator's hand
    // waiting to be moved; while set the screen prompts for the tote.
    const [toteId, set_toteId] = useState<string | null>(null);
    const [reauctionItem, set_reauctionItem] = useState<string | null>(null);
    const [reauctionEnabled, set_reauctionEnabled] = useState<boolean>(false);
    // The bay-tote lookup has answered — until then the screen must not
    // flash the tote prompt at a bay that turns out to be mapped.
    const [bayToteLoaded, set_bayToteLoaded] = useState<boolean>(false);
    // The BE forces a tote on bay entry only while the warehouse's active
    // Sorter 1 batch is 1 (Lewis, 2026-10-27: reauction items only occur in
    // batch 1). Read with bay-tote and refreshed by each close; no polling —
    // a batch flip is learnt on bay re-entry or on the next reauction item
    // scan, which asks for a tote by itself (the lazy path in doSubmit).
    const [requireTote, set_requireTote] = useState<boolean>(false);
    const [closeConfirm, set_closeConfirm] = useState<boolean>(false);
    // Unsold Grey → Portal write-off (BE migration 247). True while the item
    // on screen came back `grey: true` from the scan: confirming its slot
    // then also tells the BE (POST /zonestorage/scan/confirm), which writes
    // one unit of the SKU off Portal stock. Cleared with the item.
    const [greyItem, set_greyItem] = useState<boolean>(false);
    // Single-flight for the tote calls: a double scan must not fire two
    // moves. (Not `disabled` on the input — that HOLDS a scan and replays it.)
    const busyRef = useRef(false);

    const timer = useProgressTimer();
    const streakApi = useStreak();
    const bestTime = useBestTime("s1-best-time");
    const score = useSessionScore();

    // Resolved progress-bar target. Held in a ref so the latest value is read
    // by callbacks. Falls back to TIMER_DURATION_MS until settings load.
    const timerDurationRef = useRef(TIMER_DURATION_MS);

    useEffect(() => {
        if (!warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/settings/${encodeURIComponent(warehouse)}`,
                    {},
                );
                if (!res.ok) return;
                const data = await res.json();
                if (!alive) return;
                if (typeof data.sorter_1_timer_ms === "number" && data.sorter_1_timer_ms > 0) {
                    timerDurationRef.current = data.sorter_1_timer_ms;
                }
            } catch {
                // leave default
            }
        })();
        return () => { alive = false; };
    }, [warehouse]);

    // Is the Portal reauction move on here, and is this bay already on a
    // tote (another operator opened it, or we left and came back)? With the
    // move on, no tote and batch 1, the screen asks for one before any item
    // (Lewis's step 2.1); with a tote it goes straight to items (2.2).
    useEffect(() => {
        if (!warehouse || !bay) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/zonestorage/reauction/bay-tote`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ warehouse, bay }),
                });
                if (res.status === 401) { logoutProcessor(); navigator("/login"); return; }
                if (!res.ok) return;
                const data = await res.json();
                if (!alive) return;
                set_reauctionEnabled(data?.enabled === true);
                if (data?.enabled === true && typeof data.toteId === "string" && data.toteId) set_toteId(data.toteId);
                set_requireTote(data?.requireTote === true);
                set_bayToteLoaded(true);
            } catch {
                // feature silently off
            }
        })();
        return () => { alive = false; };
    }, [warehouse, bay, navigator]);

    // No reauction tote on this bay (yet, or any more) and the batch is 1:
    // the next scan must be a tote. A reauction unit in hand asks for a tote
    // regardless of the batch — see `reauctionItem !== null` in doSubmit.
    const needTote = reauctionEnabled && bayToteLoaded && toteId === null && requireTote;

    const [timeBonus, set_timeBonus] = useState<number | null>(null);
    const [timeBonusColor, set_timeBonusColor] = useState<"green" | "amber">("green");
    const [isBestTime, set_isBestTime] = useState<boolean>(false);
    const timeBonusRef = useRef<NodeJS.Timeout | null>(null);
    const timeBonusKeyRef = useRef<number>(0);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const closeErrorPopUp = () => {
        set_errorPopUp(false);
    };

    const reasonToast = useCallback((reason?: string | null, params: Record<string, string | number> = {}) => {
        toast.dismiss();
        toast.error(t(zoneDispatchReasonKey(reason), { status: "", order: "", tote: toteId ?? "", ...params }));
    }, [t, toteId]);

    // Score one completed full cycle (item scan → next item scan): award the
    // time bonus + streak if finished within the window, reset the streak if it
    // ran over. No-op when no cycle was running (e.g. the very first item).
    const awardCycle = useCallback(() => {
        const { elapsedMs, completed, durationMs } = timer.stop();
        if (durationMs <= 0) return;
        if (!completed) {
            const secondsLeft = Math.ceil((durationMs - elapsedMs) / 1000);
            set_timeBonusColor(elapsedMs / durationMs < 0.5 ? "green" : "amber");

            const { isNewBest, prevExisted } = bestTime.check(elapsedMs);
            set_isBestTime(isNewBest && prevExisted);

            streakApi.increment();
            score.addSeconds(secondsLeft, "sorter_1");

            timeBonusKeyRef.current += 1;
            set_timeBonus(secondsLeft);
            if (timeBonusRef.current) clearTimeout(timeBonusRef.current);
            timeBonusRef.current = setTimeout(() => {
                set_timeBonus(null);
                set_isBestTime(false);
            }, 1500);
        } else {
            set_timeBonus(null);
            set_isBestTime(false);
            streakApi.reset();
        }
    }, [timer, bestTime, streakApi, score]);

    // Tell the BE a grey item's slot was confirmed so one unit of the SKU is
    // written off Portal stock. Runs AFTER the confirm has already succeeded
    // on screen (the box is in the slot whatever the books say), so it never
    // blocks the scan loop and never fails the confirm: a 200 with nothing
    // written is a warning the operator passes to a supervisor, a failure is
    // an error toast. The write-off itself is quiet — Stock Control › Write
    // Off › History is the record (the pallet-sold precedent).
    const confirmGrey = useCallback(async (sku: string, weight: number, slot: string) => {
        try {
            const res = await apiFetch(`/zonestorage/scan/confirm`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, bay, itemSku: sku, location: slot }),
            });
            if (res.status === 401) { logoutProcessor(); navigator("/login"); return; }
            let data: any = null;
            try { data = await res.json(); } catch { data = null; }
            if (!res.ok || !data?.ok) {
                toast.dismiss(); toast.error(t("zone_grey_write_off_failed", { item: sku }));
                logger(warehouse, "S1 Loader", sku, weight, slot, "Write Off", slot, "Portal", data?.reason ?? "error");
                return;
            }
            if (data.written === true) {
                logger(warehouse, "S1 Loader", sku, weight, slot, "Write Off", data.location_name ?? data.location ?? "Portal", "Portal", "grey write-off");
                return;
            }
            toast.dismiss(); toast.warn(t("zone_grey_not_written_off", { item: sku }));
            logger(warehouse, "S1 Loader", sku, weight, slot, "Write Off", slot, "Portal", data.reason ?? "not written off");
        } catch {
            toast.dismiss(); toast.error(t("zone_grey_write_off_failed", { item: sku }));
        }
    }, [warehouse, bay, navigator, t]);

    // Move the reauction unit in hand onto the tote. On success the screen
    // returns to SCAN ITEM; `tote_not_mapped` (another bay closed the shared
    // tote) drops the tote and keeps the item so the operator just scans a
    // new tote; an item Portal does not know is handed to a supervisor.
    const doMove = useCallback(async (tote: string, sku: string, weight: number) => {
        try {
            const res = await apiFetch(`/zonestorage/reauction/move`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, bay, toteId: tote, itemSku: sku }),
            });
            if (res.status === 401) { logoutProcessor(); navigator("/login"); return; }
            let data: any = null;
            try { data = await res.json(); } catch { data = null; }
            if (res.ok && data?.ok) {
                if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
                set_scanStatus("idle");
                playSuccess();
                set_reauctionItem(null);
                set_item("");
                logger(warehouse, "S1 Loader", sku, weight, tote, "Bay Location", data.fromLocation ?? "Portal", tote, "reauction move");
                return;
            }
            const reason: string | undefined = data?.reason;
            flashScanStatus("error");
            playError();
            if (reason === "tote_not_mapped") {
                set_toteId(null);
                toast.dismiss(); toast.error(t("zone_reauction_tote_not_mapped"));
            } else if (reason === "sku_unknown") {
                set_reauctionItem(null);
                set_item("");
                toast.dismiss(); toast.error(t("zone_reauction_not_portal_item"));
            } else {
                reasonToast(reason, { tote });
            }
            logger(warehouse, "S1 Loader", sku, weight, tote, "Bay Location", "Portal", tote, reason ?? "error");
        } catch {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("scan_failed"));
        }
    }, [warehouse, bay, navigator, t, reasonToast]);

    // Yes/No on the close-tote confirm.
    const confirmClose = useCallback(async (yes: boolean) => {
        if (!yes || !toteId) { set_closeConfirm(false); return; }
        if (busyRef.current) return;
        busyRef.current = true;
        const closing = toteId;
        try {
            const res = await apiFetch(`/zonestorage/reauction/close-tote`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, bay, toteId: closing }),
            });
            if (res.status === 401) { logoutProcessor(); navigator("/login"); return; }
            let data: any = null;
            try { data = await res.json(); } catch { data = null; }
            if (res.ok && data?.ok) {
                logger(warehouse, "S1 Loader", `${data.movedUnits ?? 0} units`, 0, closing, "Close Tag", `Bay ${bay}`, "Put Away", "success");
                // Batch 1: ask for a new tote straight away; batch 2: back to
                // items. A body without the field (older BE) keeps the mount value.
                if (typeof data.requireTote === "boolean") set_requireTote(data.requireTote);
                set_toteId(null);
                playSuccess();
                toast.dismiss();
                toast.success(t(data.empty ? "zone_reauction_tote_closed_empty" : "zone_reauction_tote_closed", { tote: closing, count: data.movedUnits ?? 0 }));
            } else if (data?.reason === "tote_not_mapped") {
                // Someone else already closed it — same outcome for this bay.
                set_toteId(null);
                playError();
                toast.dismiss(); toast.error(t("zone_reauction_tote_not_mapped"));
            } else {
                playError();
                reasonToast(data?.reason, { tote: closing });
                logger(warehouse, "S1 Loader", "Reauction", 0, closing, "Close Tag", `Bay ${bay}`, "N/A", data?.reason ?? "error");
            }
        } catch {
            playError();
            toast.dismiss(); toast.error(t("scan_failed"));
        } finally {
            busyRef.current = false;
            set_closeConfirm(false);
        }
    }, [toteId, warehouse, bay, navigator, t, reasonToast]);

    const doSubmit = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        const scannedItem = val;
        setScan("");
        set_lastScannedItem(scannedItem);

        if (closeConfirm) return;

        // SCAN ITEM + the bay's own reauction tote rescanned = "close it?".
        // Before the length gate: tote ids can be short.
        if (!errorRescan && location.length === 0 && reauctionItem === null && toteId && val === toteId) {
            logger(warehouse, "S1 Loader", "Reauction", 0, val, "Close Tag", `Bay ${bay}`, "N/A", "prompt");
            set_closeConfirm(true);
            return;
        }

        // SCAN REAUCTION TOTE: the bay has no tote — on entry or after a close
        // during batch 1, or (any batch) because a reauction unit is in hand
        // with no tote to put it in. The scan opens (or joins) a tote; a
        // waiting unit is then moved onto it.
        if (toteId === null && (needTote || reauctionItem !== null)) {
            if (!isToteShaped(val)) {
                flashScanStatus("error");
                playError();
                reasonToast("not_a_tote");
                logger(warehouse, "S1 Loader", reauctionItem ?? "Reauction", itemWeight, val, "Open Tag", `Bay ${bay}`, "N/A", "error");
                return;
            }
            if (busyRef.current) return;
            busyRef.current = true;
            try {
                const res = await apiFetch(`/zonestorage/reauction/open-tote`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ warehouse, bay, toteId: val }),
                });
                if (res.status === 401) { logoutProcessor(); navigator("/login"); return; }
                let data: any = null;
                try { data = await res.json(); } catch { data = null; }
                if (res.ok && data?.ok) {
                    const opened: string = data.toteId ?? val;
                    set_toteId(opened);
                    logger(warehouse, "S1 Loader", "Reauction", 0, opened, "Open Tag", "N/A", `Bay ${bay}`, "success");
                    if (reauctionItem !== null) {
                        await doMove(opened, reauctionItem, itemWeight);
                    } else {
                        flashScanStatus("success");
                        playSuccess();
                    }
                } else {
                    flashScanStatus("error");
                    playError();
                    reasonToast(data?.reason, { tote: data?.toteId ?? val });
                    logger(warehouse, "S1 Loader", "Reauction", 0, val, "Open Tag", "N/A", `Bay ${bay}`, data?.reason ?? "error");
                }
            } catch {
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("scan_failed"));
            } finally {
                busyRef.current = false;
            }
            return;
        }

        // SCAN TOTE <id>: a reauction unit is in hand and the bay has a tote.
        if (reauctionItem !== null && toteId !== null) {
            if (val === toteId) {
                if (busyRef.current) return;
                busyRef.current = true;
                try { await doMove(toteId, reauctionItem, itemWeight); }
                finally { busyRef.current = false; }
                return;
            }
            flashScanStatus("error");
            playError();
            if (val === reauctionItem) {
                logger(warehouse, "S1 Loader", reauctionItem, itemWeight, val, "Bay Location", "Hand", toteId, "repeat");
            } else {
                toast.dismiss(); toast.error(t("zone_reauction_wrong_tote", { tote: toteId }));
                logger(warehouse, "S1 Loader", reauctionItem, itemWeight, val, "Bay Location", "Hand", toteId, "wrong location");
            }
            return;
        }

        if (scannedItem.length < 5 || scannedItem.length > 20) {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("invalid_scan"));
            return;
        }

        if (errorRescan) {
            if (val === item) {
                flashScanStatus("success");
                playSuccess();
                set_errorRescan(false);
            } else {
                flashScanStatus("error");
                playError();
            }
            return;
        }

        if (location.length > 0) {
            if (val.slice(-2) === location[0]) {
                // Placement confirmed. The cycle timer is NOT stopped here — it
                // keeps running until the next item is scanned (the full cycle),
                // which is when the bonus is awarded.
                logger(warehouse, "S1 Loader", item, itemWeight, scannedItem, "Bay Location", "Hand", scannedItem, "success");
                // Placement confirmed — black out THIS bay's beam. The physical
                // labels repeat across bays ("C3" exists on every bay), but the
                // operator declared their bay by scanning it to start, so
                // compose the bay-numbered slot barcode the aim path keys on
                // (mirrors the BE's sorter1Location: {prefix}S1{bay}{code}).
                // Best-effort, same as the aim that lit it: a miss just leaves
                // the dwell to recentre the head.
                const warehousePrefix = warehouse === "Newport" ? "NP" : warehouse === "Poland" ? "PL" : warehouse === "Pembroke" ? "PD" : "TT";
                apiFetch(`/zonestorage/scan/aim`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ location: `${warehousePrefix}S1${String(bay).padStart(2, "0")}${location[0]}`, off: true }),
                }).catch(() => { /* never block a scan on the light */ });
                // Location confirmed — jump straight back to SCAN ITEM with no
                // success-flash delay (the green tick is gone, so the 500ms flash
                // would only blank the prompt). Audio still confirms the placement.
                if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
                set_scanStatus("idle");
                playSuccess();
                set_Location([]);
                set_item("");
                // Unsold Grey: the confirmed placement writes the unit off
                // Portal stock. Not awaited — the prompt is already back on
                // SCAN ITEM and the next scan must not wait on Postgres.
                if (greyItem) {
                    set_greyItem(false);
                    void confirmGrey(item, itemWeight, location[0]);
                }
            } else if (scannedItem === item) {
                logger(warehouse, "S1 Loader", item, itemWeight, scannedItem, "Bay Location", "Hand", scannedItem, "repeat");
                flashScanStatus("error");
                playError();
            } else {
                logger(warehouse, "S1 Loader", item, itemWeight, scannedItem, "Bay Location", "Hand", scannedItem, "wrong location");
                flashScanStatus("error");
                playError();
                set_errorPopUp(true);
                set_errorRescan(true);
                timer.reset();
            }

            return;
        }

        await apiFetch(`/zonestorage/scan`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ warehouse: warehouse, sorter: 1, bay: bay, itemSku: scannedItem })
        }).then(async (res): Promise<any> => {
            let response = await res.json();

            if (res.ok) {
                // This item scan ends the previous full cycle — score it, then
                // start the next cycle (runs until the next item is scanned).
                awardCycle();
                if (response.reauction === true) {
                    // Unsold Reauction: the unit goes onto the bay's reauction
                    // tote, not a slot. The tote walk is not the timed slot
                    // motion, so no cycle timer is started for it (the next
                    // normal item's awardCycle is then a no-op).
                    set_item(scannedItem);
                    set_itemWeight(response.weight ?? 0);
                    set_greyItem(false);
                    set_reauctionItem(scannedItem);
                    const beTote: string | null = typeof response.toteId === "string" && response.toteId ? response.toteId : null;
                    // The BE's mapping is the truth: a tote another bay closed,
                    // or one another operator opened on this bay, lands here.
                    if (beTote !== toteId) set_toteId(beTote);
                    logger(warehouse, "S1 Loader", scannedItem, response.weight ?? 0, scannedItem, "Item", "Zone", beTote ?? "Hand", "reauction");
                    flashScanStatus("success");
                    playSuccess();
                    return;
                }
                set_Location([response.location.toString()]);
                set_item(scannedItem);
                set_itemWeight(response.weight);
                // Unsold Grey with the warehouse switch on: the slot flow is
                // the same, but the confirm also writes the unit off.
                set_greyItem(response.grey === true);
                logger(warehouse, "S1 Loader", scannedItem, response.weight, scannedItem, "Item", "Zone", "Hand", response.grey === true ? "grey" : "success");
                flashScanStatus("success");
                playSuccess();
                timer.start(timerDurationRef.current);
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                set_greyItem(false);
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("server_error"));
                logger(warehouse, "S1 Loader", scannedItem, 0, scannedItem, "Item", "Zone", "Hand", "error");
            }
        })
    }, [scan, item, itemWeight, location, errorRescan, warehouse, bay, navigator, t, timer, awardCycle,
        toteId, reauctionItem, needTote, closeConfirm, doMove, reasonToast, greyItem, confirmGrey]);

    // SCAN REAUCTION TOTE (no tote on the bay) or SCAN TOTE <id> (reauction
    // unit in hand) — either replaces the SCAN ITEM prompt.
    const reauctionPrompt = (needTote || reauctionItem !== null) && errorRescan === false && location.length === 0;
    const title = location.length > 0
        ? t("scan_location")
        : reauctionPrompt
            ? (toteId ? t("scan_tote_id", { tote: toteId }) : t("scan_reauction_tote"))
            : t("scan_item");

    return (
        <div className="scan-panel">
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{title}</h1>
                {/* Group the score pill with the back button (matches Pick & Sort) so it
                    doesn't crowd / overlap the title — consistent gap on the right. */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ScoreDisplay total={score.total} />
                    <button className="page-back-btn" onClick={resetFunc}><ArrowLeft size={20} /></button>
                </div>
            </div>

            <TimeBonusPopup
                seconds={timeBonus}
                color={timeBonusColor}
                streak={streakApi.streak}
                isBest={isBestTime}
                bumpKey={timeBonusKeyRef.current}
            />

            <ProgressBar
                progress={timer.state.progress}
                visible={timer.state.active && !errorRescan}
            />

            <div className="scan-centre">
                {/* Suppress the green success tick — only error (red X) shows positive/negative feedback here. */}
                {(location.length === 0 || errorRescan) && <ScanStatusIcons status={scanStatus === "success" || scanStatus === "error" ? "idle" : scanStatus} />}

                {(location.length === 0 && errorRescan === false && !reauctionPrompt) && (
                    <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {t("scan_item").toUpperCase().split(" ").map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                )}
                {reauctionPrompt && (
                    // Name the tote to scan (or ask for a new one). With a
                    // reauction unit in hand, keep it on screen so the operator
                    // knows which unit this is about.
                    <>
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {(toteId ? t("scan_tote_id", { tote: toteId }) : t("scan_reauction_tote")).toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        {reauctionItem !== null && (
                            <div className={`scan-feedback-area ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                                <h3>{reauctionItem}</h3>
                            </div>
                        )}
                    </>
                )}
                {(location.length > 0 && errorRescan === false) && (
                    // Destination boxes shown at the top — same position the SCAN ITEM
                    // prompt occupied — matching Sorter 2 and Pick & Sort (no large
                    // "SCAN LOCATION" text above them).
                    <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                        {location.map((loc) =>
                            <ZoneSorterLocationBoxes key={loc} first={loc.charAt(0)} second={loc.charAt(1)} />
                        )}
                    </div>
                )}

                {errorRescan && (
                    <>
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("scan_item").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <div className={`scan-feedback-area ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            <h3>{item}</h3>
                        </div>
                    </>
                )}

                <div className="scan-feedback-area">
                    <p className="tiny-gap align-center">{t("scanned_label", { item: lastScannedItem })}</p>
                    {reauctionEnabled && toteId && (
                        <p className="tiny-gap align-center zone-reauction-tote-label">{t("reauction_tote_label", { tote: toteId })}</p>
                    )}
                </div>
            </div>

            <HiddenScanInput value={scan} onChange={setScan} onSubmit={doSubmit} disabled={errorPopUp || closeConfirm} disableCamera />

            {errorPopUp &&
                <div className="scan-error-pop-up" onClick={() => closeErrorPopUp()}>
                    <div className="scan-error-pop-up-container" onClick={(e) => e.stopPropagation()}>
                        <button className="scan-error-close-btn" onClick={() => closeErrorPopUp()}><X size={28} strokeWidth={2.5} /></button>
                        <h1>{t("wrong_location")}</h1>
                        <h2>{item}</h2>
                    </div>
                </div>
            }

            {closeConfirm && toteId &&
                // Rescanned the bay's reauction tote on SCAN ITEM: close it?
                <div className="scan-error-pop-up scan-confirm-pop-up" onClick={() => confirmClose(false)}>
                    <div className="scan-error-pop-up-container" onClick={(e) => e.stopPropagation()}>
                        <h1>{t("zone_reauction_close_title")}</h1>
                        <h2>{toteId}</h2>
                        <h3>{t("zone_reauction_close_body")}</h3>
                        <div className="conveyor-btn-group">
                            <button className="conveyor-yes-btn" onClick={(e) => { e.preventDefault(); confirmClose(true); }}><Check size={24} strokeWidth={3} />{t("yes")}</button>
                            <button className="conveyor-no-btn" onClick={(e) => { e.preventDefault(); confirmClose(false); }}><X size={24} strokeWidth={3} />{t("no")}</button>
                        </div>
                    </div>
                </div>
            }

        </div>
    )
}

export default ZoneStorageSorter1
